// WHAT: Component for displaying a single notification item
// WHY:  Encapsulates notification display logic with mark as read functionality
// DOMAIN: Admin → Notifications
// DEPENDENCIES: Notification type
// RISKS:
//   - NOTE: onRead callback is called without error handling
//   - NOTE: No loading state during mark as read operation

import { type FC } from "react";

import { type Notification } from "../types";

/**
 * Props for NotificationItem component
 */
interface Properties {
  /** Notification data to display */
  notification: Notification;
  /** Callback to mark notification as read */
  onRead: () => void;
}

/**
 * WHAT:
 *   Component that displays a single notification with title, message,
 *   and a button to mark it as read (if unread).
 *
 * WHY:
 *   Provides a reusable component for rendering notification items in a list,
 *   with conditional rendering of the mark as read button.
 *
 * PROPS:
 *   - notification: Notification — Notification data to display
 *   - onRead: () => void — Handler to mark notification as read
 *
 * BEHAVIOR:
 *   - Displays notification title and message
 *
 * @returns {JSX.Element} Die gerenderte NotificationItem-Komponente
 *   - Shows "Mark as read" button only if notification is unread
 *   - Calls onRead callback when button is clicked
 *
 * RISKS:
 *   - NOTE: onRead callback should handle async operations and errors
 *   - NOTE: No visual feedback during mark as read operation
 *
 * @param root0
 *
 * @param root0.notification
 *
 * @param root0.onRead
 *
 */
export const NotificationItem: FC<Properties> = ({ notification, onRead }) => (
  <li className="flex items-center justify-between rounded-md p-3 bg-slate-800 text-white">
    <div>
      <p className="font-brand-medium">{notification.title}</p>
      <p className="text-sm opacity-80">{notification.message}</p>
    </div>
    {!notification.read && (
      <button className="btn-primary text-sm" onClick={onRead}>
        Mark as read
      </button>
    )}
  </li>
);
